import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { MetricsContext, TemplateStat } from "contexts/Metrics";
import { OnboardingDialogsContext, Page } from "contexts/OnboardingDialogs";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { QuickSendContext } from "contexts/QuickSend";
import { useContext, useEffect, useMemo, useState } from "react";
import { Route, Routes, useNavigate, useSearchParams } from "react-router-dom";
import { ALL } from "schemas/forms";
import { SetAlertType } from "schemas/functions";
import { routes } from "schemas/routes";

import { PAGE_VISITED } from "constants/trackingProps";
import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";
import { removeFromList } from "utils/updateLocalState";
import { useGamification } from "utils/useGamification";

import DeleteTemplateDialog from "./DeleteTemplate";
import EditTemplateDialog from "./EditTemplate";
import EvaluatorSheet from "./EvaluatorSheet";
import FolderChips, { FolderSelection } from "./FolderChips";
import MoveToFolderMenu from "./MoveToFolderMenu";
import StatsBar from "./StatsBar";
import TemplateCard from "./TemplateCard";
import styles from "./styles";

interface TemplatesTableProps {
  setAlert: SetAlertType;
}
export default function TemplatesTable({ setAlert }: TemplatesTableProps) {
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const { setErrorAlert } = useContext(AlertContext);
  const { openAssistant } = useContext(DiscoverViewContext);

  const { currentOrg, setEmailSettings } = useContext(OrganizationUserContext);
  const {
    fetchOutreachTemplates,
    templates,
    setTemplates,
    fetchOutreachTemplatesLoading,
    outreachTemplates,
    setCreateTemplateDialogOpen,
    setCurrentTemplateContentType,
    fetchFolders,
  } = useContext(OutreachTemplatesContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const { setNewlyAddedTemplateIndex } = useContext(QuickSendContext);
  const { templateStats, fetchMetricsByTemplate } = useContext(MetricsContext);
  const { setCreateTemplateOnboardingDialogOpen, setPage } = useContext(
    OnboardingDialogsContext,
  );

  const [templateToDelete, setTemplateToDelete] = useState<Template | null>(
    null,
  );
  const [deleteTemplateLoading, setDeleteTemplateLoading] = useState(false);
  const [fetchTemplateLoading, setFetchTemplateLoading] = useState<
    number | null
  >(null);
  const [filter, setFilter] = useState(ALL);
  const [selectedFolderId, setSelectedFolderId] =
    useState<FolderSelection>(ALL);
  const [moveAnchorEl, setMoveAnchorEl] = useState<null | HTMLElement>(null);
  const [moveTemplate, setMoveTemplate] = useState<Template | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuTemplate, setMenuTemplate] = useState<Template | null>(null);
  const [totalPitchesSent, setTotalPitchesSent] = useState<number | null>(null);
  const [evaluatorOpen, setEvaluatorOpen] = useState(false);

  const selectFolder = (sel: FolderSelection) => {
    setSelectedFolderId(sel);
    setFilter(ALL);
  };

  const selectContentFilter = (value: typeof ALL | TemplateContentType) => {
    setFilter(filter === value ? ALL : value);
    setSelectedFolderId(ALL);
  };
  const [emailCounts, setEmailCounts] = useState<{ [key: number]: number }>({});
  const { addTemplateTaskCompleted } = useGamification();

  useEffect(() => {
    if (currentOrg?.id) {
      // If Organization is VA user, we want to fetch niches
      // along with templates.
      fetchOutreachTemplates();
      fetchFolders();
      fetchEmailCount();
      fetchMetricsByTemplate();
      fetchTemplateStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  useEffect(() => {
    trackEvent(PAGE_VISITED);
  }, []);

  useEffect(() => {
    const openCreate = searchParams.get("openCreate");
    if (openCreate !== "outreach" && openCreate !== "followup") {
      return;
    }
    setCurrentTemplateContentType(
      openCreate === "followup"
        ? TemplateContentType.FOLLOWUP
        : TemplateContentType.OUTREACH,
    );
    setPage(Page.fromScratch);
    setCreateTemplateDialogOpen(true);
    searchParams.delete("openCreate");
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (searchParams.get("startOnboarding") !== "1") {
      return;
    }
    setCreateTemplateOnboardingDialogOpen(true);
    searchParams.delete("startOnboarding");
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const noTemplatesFound =
    outreachTemplates?.length === 0 && !fetchOutreachTemplatesLoading;
  useEffect(() => {
    if (noTemplatesFound) {
      trackCreateTemplate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noTemplatesFound]);

  const templateTypeCounts = useMemo(() => {
    const withIds = templates?.filter((x) => x.id) ?? [];
    const outreach = withIds.filter(
      (x) => x.contentType === TemplateContentType.OUTREACH,
    ).length;
    const followup = withIds.filter(
      (x) => x.contentType === TemplateContentType.FOLLOWUP,
    ).length;
    const evaluated = withIds.filter(
      (x) => (x.rubrics?.length ?? 0) > 0,
    ).length;
    return { outreach, followup, total: outreach + followup, evaluated };
  }, [templates]);

  const statsByTemplateId = useMemo(() => {
    const map: { [key: number]: TemplateStat } = {};
    templateStats?.forEach((stat) => {
      map[stat.templateId] = stat;
    });
    return map;
  }, [templateStats]);

  const bestRates = useMemo(() => {
    const nameById = new Map<number, string>();
    templates?.forEach((t) => {
      if (t.id) nameById.set(t.id, t.name);
    });
    const existingStats = (templateStats ?? []).filter((s) =>
      nameById.has(s.templateId),
    );
    const best = (pick: (s: TemplateStat) => number) => {
      const top = existingStats.reduce<TemplateStat | null>(
        (max, s) => (max === null || pick(s) > pick(max) ? s : max),
        null,
      );
      return top && pick(top) > 0
        ? { value: pick(top), templateName: nameById.get(top.templateId) }
        : null;
    };
    return {
      openRate: best((s) => s.openedPercentage),
      replyRate: best((s) => s.respondedPercentage),
    };
  }, [templateStats, templates]);

  const renderContentChip = (
    value: typeof ALL | TemplateContentType,
    label: string,
  ) => {
    const withIds = templates?.filter((x) => x.id);
    const count =
      value === ALL
        ? withIds?.length ?? 0
        : withIds?.filter((x) => x.contentType === value).length ?? 0;
    const active =
      value === ALL
        ? filter === ALL && selectedFolderId === ALL
        : filter === value;
    return (
      <Box
        sx={styles.tab(active)}
        onClick={() => {
          if (value === ALL) {
            setFilter(ALL);
            setSelectedFolderId(ALL);
          } else {
            selectContentFilter(value);
          }
        }}
      >
        <Typography sx={styles.tabLabel(active)}>{label}</Typography>
        <Typography sx={styles.tabCount(active)}>{count}</Typography>
      </Box>
    );
  };

  const getFilteredTemplates = () => {
    let templatesWithIds = templates?.filter((x) => x.id);
    if (
      filter === TemplateContentType.OUTREACH ||
      filter === TemplateContentType.FOLLOWUP
    ) {
      templatesWithIds = templatesWithIds?.filter(
        (x) => x.contentType === filter,
      );
    }
    if (selectedFolderId !== ALL) {
      templatesWithIds = templatesWithIds?.filter(
        (x) => x.folderId === selectedFolderId,
      );
    }
    return templatesWithIds;
  };

  const handleOpenDeleteTemplate = async (template: Template) => {
    if (!currentOrg?.id) {
      return;
    }
    if (
      outreachTemplates?.length <= 1 &&
      template?.contentType === TemplateContentType.OUTREACH
    ) {
      setAlert("Need at least one outreach template", "warning");
      return;
    }
    trackEvent("Delete Template Button Clicked", {
      "Template ID": template.id,
    });
    if (template?.id) setFetchTemplateLoading(template?.id);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates/${template.id}`,
      );
      setTemplateToDelete(res.template);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setFetchTemplateLoading(null);
    }
  };

  const handleDeleteTemplate = async (replacedTemplateId: number | null) => {
    setDeleteTemplateLoading(true);
    try {
      const { emailSetting } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates/${templateToDelete?.id}`,
        "DELETE",
        {},
        {
          replacedTemplateId,
        },
      );
      if (emailSetting) setEmailSettings(emailSetting);
      removeFromList(templates, setTemplates, templateToDelete, "id");
      fetchOutreachTemplates();
      handleClose();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setDeleteTemplateLoading(false);
    }
  };

  const handleClose = () => {
    setTemplateToDelete(null);
  };

  const trackCreateTemplate = () => {
    trackEvent("Add Template Button Clicked", {
      "Clicked From": "Templates Table View",
    });

    if (outreachTemplates?.length === 0) {
      setCreateTemplateOnboardingDialogOpen(true);
      return;
    }
    setCreateTemplateDialogOpen(true);
    setNewlyAddedTemplateIndex(-1);
  };

  const handleCopyTemplate = async (template: Template) => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates`,
        "POST",
        {},
        {
          name: `${template?.name} - Copy`,
          subject: template?.subject,
          body: template?.body,
          contentType: template?.contentType,
          attachment: template?.attachment,
        },
      );
      setTemplates((prev) => [...prev, res.template]);
      navigate(`/${routes.templates}/${res.template.id}`);
      addTemplateTaskCompleted(template.contentType);
      setAlert("Successfully duplicated template", "success");
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const handleTemplateClick = (template: Template) => {
    navigate(`/${routes.templates}/${template.id}`);
  };

  const onTemplateSuccess = () => {
    navigate(`/${routes.templates}`, { replace: true });
  };

  const fetchEmailCount = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates/scheduled-email-counts`,
        "GET",
      );
      setEmailCounts(res.scheduledEmailCounts);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const fetchTemplateStats = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates/stats`,
        "GET",
      );
      setTotalPitchesSent(res.totalPitchesSent);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  return (
    <Box sx={styles.root}>
      <Box sx={styles.headerRow}>
        <Box>
          <Typography sx={styles.headerTitle}>Your Templates</Typography>
          <Typography sx={styles.headerSubtitle}>
            Create and manage your outreach templates
          </Typography>
        </Box>
        <Box sx={styles.headerActions}>
          <Button
            disableElevation
            sx={styles.aiAssistantButton}
            onClick={() =>
              openAssistant({
                promptPage: "templates",
                source: "Templates CTA",
              })
            }
            startIcon={
              <Box
                component="i"
                className="fa-solid fa-sparkles"
                sx={{ fontSize: 13 }}
              />
            }
          >
            AI Assistant
          </Button>
          <Button
            disableElevation
            sx={styles.createTemplateButton}
            onClick={trackCreateTemplate}
            startIcon={
              <Box
                component="i"
                className="fa-regular fa-plus"
                sx={{ fontSize: 13 }}
              />
            }
          >
            Create Template
          </Button>
        </Box>
      </Box>

      <StatsBar
        totalPitchesSent={totalPitchesSent}
        outreachTemplateCount={templateTypeCounts.outreach}
        followupTemplateCount={templateTypeCounts.followup}
        bestOpenRate={bestRates.openRate}
        bestReplyRate={bestRates.replyRate}
        templatesEvaluated={templateTypeCounts.evaluated}
        totalTemplates={templateTypeCounts.total}
        onEvaluatedClick={() => setEvaluatorOpen(true)}
      />

      <Box sx={styles.controlsRow}>
        <Box sx={styles.tabsContainer}>
          {renderContentChip(ALL, "All")}
          {renderContentChip(TemplateContentType.OUTREACH, "Outreach")}
          {renderContentChip(TemplateContentType.FOLLOWUP, "Follow-up")}
        </Box>

        <Box sx={styles.controlsDivider} />

        <FolderChips
          templates={templates ?? []}
          selectedFolderId={selectedFolderId}
          setSelectedFolderId={selectFolder}
          setAlert={setAlert}
        />
      </Box>

      {fetchOutreachTemplatesLoading ? (
        <CircularProgress />
      ) : (
        <Box sx={styles.cardGrid}>
          {(getFilteredTemplates() ?? []).map((template: Template) => (
            <Card
              key={template.id}
              sx={styles.card}
              onClick={() => handleTemplateClick(template)}
            >
              <TemplateCard
                template={template}
                emailCounts={emailCounts}
                stat={template.id ? statsByTemplateId[template.id] : undefined}
                onMenuOpen={(e, t) => {
                  setMenuAnchorEl(e.currentTarget);
                  setMenuTemplate(t);
                }}
              />
            </Card>
          ))}
          <Box sx={styles.createCard} onClick={trackCreateTemplate}>
            <Box
              component="i"
              className="fa-regular fa-plus"
              sx={{ fontSize: 20 }}
            />
            <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
              Create new template
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#717182" }}>
              Start from scratch or use a prompt
            </Typography>
          </Box>
        </Box>
      )}

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => {
          setMenuAnchorEl(null);
          setMenuTemplate(null);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem
          onClick={() => {
            if (menuTemplate) {
              trackEvent("Template Edited", {
                "Template ID": menuTemplate.id,
              });
              handleTemplateClick(menuTemplate);
            }
            setMenuAnchorEl(null);
            setMenuTemplate(null);
          }}
        >
          <ListItemIcon>
            <Box component="i" className="fa-regular fa-pen" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuTemplate) {
              trackEvent("Template Duplicated", {
                "Template ID": menuTemplate.id,
              });
              handleCopyTemplate(menuTemplate);
            }
            setMenuAnchorEl(null);
            setMenuTemplate(null);
          }}
        >
          <ListItemIcon>
            <Box component="i" className="fa-regular fa-clipboard" />
          </ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuTemplate && menuAnchorEl) {
              setMoveAnchorEl(menuAnchorEl);
              setMoveTemplate(menuTemplate);
            }
            setMenuAnchorEl(null);
            setMenuTemplate(null);
          }}
        >
          <ListItemIcon>
            <Box component="i" className="fa-regular fa-folder" />
          </ListItemIcon>
          <ListItemText>Move to folder</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            if (menuTemplate) {
              handleOpenDeleteTemplate(menuTemplate);
            }
            setMenuAnchorEl(null);
            setMenuTemplate(null);
          }}
          disabled={fetchTemplateLoading === menuTemplate?.id}
        >
          <ListItemIcon sx={{ color: "error.main" }}>
            {fetchTemplateLoading === menuTemplate?.id ? (
              <CircularProgress size={16} />
            ) : (
              <Box component="i" className="fa-regular fa-trash-can" />
            )}
          </ListItemIcon>
          <ListItemText
            sx={{
              "& .MuiTypography-root": {
                color: "error.main",
              },
            }}
          >
            Delete
          </ListItemText>
        </MenuItem>
      </Menu>

      <Routes>
        <Route
          path=":templateId"
          element={<EditTemplateDialog onSuccess={onTemplateSuccess} />}
        />
      </Routes>
      {moveAnchorEl && moveTemplate && (
        <MoveToFolderMenu
          anchorEl={moveAnchorEl}
          template={moveTemplate}
          onClose={() => {
            setMoveAnchorEl(null);
            setMoveTemplate(null);
          }}
          onMoved={() => fetchOutreachTemplates(true)}
          setAlert={setAlert}
        />
      )}
      {templateToDelete && (
        <DeleteTemplateDialog
          open={Boolean(templateToDelete?.id)}
          handleClose={() => setTemplateToDelete(null)}
          template={templateToDelete}
          handleConfirm={(replacedTemplateId: number | null) => {
            handleDeleteTemplate(replacedTemplateId);
          }}
          loading={deleteTemplateLoading}
        />
      )}

      <EvaluatorSheet
        open={evaluatorOpen}
        onClose={() => setEvaluatorOpen(false)}
        templates={templates ?? []}
        statsByTemplateId={statsByTemplateId}
        onSelectTemplate={handleTemplateClick}
      />
    </Box>
  );
}
